import { columnIds, useSearch } from './use-search'
import { SearchTab } from './constants/search-tabs'
import Breadcrumb from 'lib/components/breadcrumb/breadcrumb'
import { AnimatedLoadingScreen, EmptySearchScreen } from '../requests/empty-screens'
import Filters from './filters'
import Button, { LinkLikeButton } from 'components/core/button'
import { FolderOpenIcon, ImageIcon, LoaderCircleIcon, TicketIcon } from 'lucide-react'
import { useFirstRender } from './use-first-render'
import ConfirmationModal from 'components/elements/confirmation-modal'
import { useConfirmationModalContext } from 'providers/confirmation-modal-provider'
import SearchRequestsTable from './search-requests-table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'lib/components/tabs'
import { useFeatureFlagsContext } from 'lib/components/feature-flags/feature-flags-provider'
import { updateParams } from 'lib/util/url'
import ProjectList from 'components/elements/projects/project-list'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import Badge from 'lib/components/badge/badge'
import Pagination from 'components/elements/pagination'
import SavedSearches from './saved-searches'
import { FilesList } from 'components/elements/files/files-list'
import { FilesProvider } from 'components/elements/files/files-context'

const AssetsSection = ({
  short,
  useSearchRef,
  currentTab,
  setTab,
}: {
  short?: boolean
  useSearchRef: ReturnType<typeof useSearch>
  currentTab: SearchTab
  setTab: (tab: SearchTab) => void
}) => {
  const { assetsQuery, assetsCount, pagination, table, assets } = useSearchRef

  return (
    <FilesProvider
      onFinish={() => {
        let count = 0
        assetsQuery.refetch()
        const interval = setInterval(() => {
          assetsQuery.refetch()
          count++
          if (count >= 2) {
            clearInterval(interval)
          }
        }, 1000)
      }}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
        <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1 tw-p-0">
          <ImageIcon />
          Assets ({assetsCount})
        </h4>
        {currentTab !== SearchTab.ASSETS && (
          <LinkLikeButton onClick={() => setTab(SearchTab.ASSETS)}>See all</LinkLikeButton>
        )}
      </div>
      {assetsCount !== 0 && <FilesList files={short ? assets?.slice(0, 5) : assets} />}
      {!short && (
        <Pagination
          pageCount={assetsCount / pagination.pageSize}
          onPageChange={({ selected }) => {
            table.setPageIndex(selected + 1)
          }}
          forcePage={pagination.pageIndex - 1}
        />
      )}
    </FilesProvider>
  )
}

export const getResultsTitle = (search: string, count: number) => {
  if (search) {
    return `Results for "${search.slice(0, 50)}${search.length > 50 ? '...' : ''}" (${count})`
  }
  return `Results (${count})`
}

export const Search = () => {
  const useSearchRef = useSearch()
  const {
    ticketsCount,
    projectsCount,
    pagination,
    projectsLoading,
    ticketsLoading,
    projects,
    error,
    table,
    assetsLoading,
    assetsCount,
  } = useSearchRef
  const { isModalVisible, modalData, setModalVisible } = useConfirmationModalContext()
  const { loading: loadingFirstRender } = useFirstRender()
  const queryClient = useQueryClient()
  const { isFeatureFlagEnabled } = useFeatureFlagsContext()
  const params = new URLSearchParams(window.location.search)
  const [currentTab, setCurrentTab] = useState<SearchTab>((params.get('tab') as SearchTab) || SearchTab.ALL)

  const damEnabled = isFeatureFlagEnabled('dam_add_modal')

  const errorScreen = error && (
    <div className="tw-mt-4 tw-flex tw-max-w-prose tw-flex-col tw-items-center tw-justify-center">
      <AnimatedLoadingScreen
        variant="warning"
        text="Something went wrong."
        subText={
          <span>
            Try refreshing the page, checking back later, or contacting support at{' '}
            <a href="mailto:help@designpickle.com">help@designpickle.com</a>.
          </span>
        }
      />
      <Button
        onClick={() => {
          window.location.reload()
        }}
        color="neutral"
        className="tw-mt-4"
      >
        Try again
      </Button>
    </div>
  )

  const setTab = (tab) => {
    if (Object.values(SearchTab).includes(tab)) {
      const newParams = new URLSearchParams(window.location.search)
      newParams.set('tab', tab)
      updateParams(newParams)
      setCurrentTab(tab)
      table.setPageIndex(1)
    }
  }

  const updateProjectsResults = () => {
    queryClient.invalidateQueries({ queryKey: ['search_projects'] })
    let count = 0
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['search_projects'] })
      count++
      if (count >= 2) {
        clearInterval(interval)
      }
    }, 1000)
  }

  const ProjectsSection = ({ short }: { short?: boolean }) => (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
        <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1 tw-p-0">
          <FolderOpenIcon />
          Projects ({projectsCount})
        </h4>
        {currentTab !== SearchTab.PROJECTS && (
          <LinkLikeButton onClick={() => setTab(SearchTab.PROJECTS)}>See all</LinkLikeButton>
        )}
      </div>
      {projectsCount !== 0 && (
        <ProjectList projects={short ? projects?.slice(0, 5) : projects} onUpdate={updateProjectsResults} />
      )}
      {!short && (
        <Pagination
          pageCount={projectsCount / pagination.pageSize}
          onPageChange={({ selected }) => {
            table.setPageIndex(selected + 1)
          }}
          forcePage={pagination.pageIndex - 1}
        />
      )}
    </>
  )

  const TicketsHeader = () => (
    <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
      <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1 tw-p-0">
        <TicketIcon />
        Requests ({ticketsCount})
      </h4>
      {currentTab !== SearchTab.REQUESTS && (
        <LinkLikeButton onClick={() => setTab(SearchTab.REQUESTS)}>See all</LinkLikeButton>
      )}
    </div>
  )

  const EmptyScreen = () => (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
      <EmptySearchScreen />
    </div>
  )

  if (loadingFirstRender) {
    return (
      <div className="tw-flex tw-h-[60vh] tw-items-center tw-justify-center">
        <AnimatedLoadingScreen
          variant="spinner"
          text="We're searching your content..."
          subText="Hang tight while we locate what you're looking for."
        />
      </div>
    )
  }

  return (
    <>
      <div id="selection-toolbar-project-drawer-container" />

      <ConfirmationModal
        title={modalData['title']}
        message={modalData['message']}
        confirmBtnText={modalData['confirmBtnText']}
        confirmAction={modalData['confirmAction']}
        cancelAction={() => setModalVisible(false)}
        visible={isModalVisible}
      />
      <Breadcrumb className="tw-mt-3" text="Back to requests" href={`/requests`} />
      <Tabs value={currentTab} onChange={setTab}>
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
            <h1 className="tw-m-0 tw-mt-2 tw-p-0 tw-text-2xl">
              {getResultsTitle(
                Array.isArray(table.getColumn(columnIds.query).getFilterValue())
                  ? table.getColumn(columnIds.query).getFilterValue()[0]
                  : table.getColumn(columnIds.query).getFilterValue(),
                ticketsCount + projectsCount,
              )}
            </h1>
            <SavedSearches setFilters={table.setColumnFilters} />
          </div>
          <div>
            <TabsList className="tw-my-2">
              <TabsTrigger value={SearchTab.ALL} onClick={() => setTab(SearchTab.ALL)}>
                All&nbsp;
                <Badge color="white" fontWeight="normal" style="rounded">
                  {projectsLoading || ticketsLoading || assetsLoading ? (
                    <LoaderCircleIcon className="lu-sm tw-animate-spin" />
                  ) : (
                    projectsCount + ticketsCount + (assetsCount || 0)
                  )}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value={SearchTab.PROJECTS} onClick={() => setTab(SearchTab.PROJECTS)}>
                Projects&nbsp;
                <Badge color="white" fontWeight="normal" style="rounded">
                  {projectsLoading ? <LoaderCircleIcon className="lu-sm tw-animate-spin" /> : projectsCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value={SearchTab.REQUESTS} onClick={() => setTab(SearchTab.REQUESTS)}>
                Requests&nbsp;
                <Badge color="white" fontWeight="normal" style="rounded">
                  {ticketsLoading ? <LoaderCircleIcon className="lu-sm tw-animate-spin" /> : ticketsCount}
                </Badge>
              </TabsTrigger>
              {damEnabled && (
                <TabsTrigger value={SearchTab.ASSETS} onClick={() => setTab(SearchTab.ASSETS)}>
                  Assets&nbsp;
                  <Badge color="white" fontWeight="normal" style="rounded">
                    {assetsLoading ? <LoaderCircleIcon className="lu-sm tw-animate-spin" /> : assetsCount}
                  </Badge>
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          <div className="tw-flex tw-items-center tw-py-2">
            <Filters table={table} currentTab={currentTab} />
          </div>
        </div>
        {errorScreen ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center"> {errorScreen}</div>
        ) : (
          <>
            <TabsContent value={SearchTab.ALL}>
              <ProjectsSection short />
              <TicketsHeader />
              {ticketsCount === 0 ? <EmptyScreen /> : <SearchRequestsTable useSearch={useSearchRef} hidePagination />}
              <AssetsSection short useSearchRef={useSearchRef} currentTab={currentTab} setTab={setTab} />
            </TabsContent>
            <TabsContent value={SearchTab.PROJECTS}>
              {projectsLoading ? (
                <AnimatedLoadingScreen
                  variant="spinner"
                  text="We're searching your content..."
                  subText="Hang tight while we locate what you're looking for."
                />
              ) : projectsCount === 0 ? (
                <EmptyScreen />
              ) : (
                <ProjectsSection />
              )}
            </TabsContent>
            <TabsContent value={SearchTab.REQUESTS}>
              {ticketsLoading ? (
                <AnimatedLoadingScreen
                  variant="spinner"
                  text="We're searching your content..."
                  subText="Hang tight while we locate what you're looking for."
                />
              ) : ticketsCount === 0 ? (
                <EmptyScreen />
              ) : (
                <>
                  <TicketsHeader />
                  <SearchRequestsTable useSearch={useSearchRef} />
                </>
              )}
            </TabsContent>
            {damEnabled && (
              <TabsContent value={SearchTab.ASSETS}>
                {assetsLoading ? (
                  <AnimatedLoadingScreen
                    variant="spinner"
                    text="We're searching your content..."
                    subText="Hang tight while we locate what you're looking for."
                  />
                ) : assetsCount === 0 ? (
                  <EmptyScreen />
                ) : (
                  <AssetsSection useSearchRef={useSearchRef} currentTab={currentTab} setTab={setTab} />
                )}
              </TabsContent>
            )}
          </>
        )}
      </Tabs>
    </>
  )
}
